var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { FontStyle } from '@inter-ikea-kompis/themes';
import { Hyperlink } from '@inter-ikea-kompis/component-hyperlink';
import { KeyboardInjectorFinder } from '@inter-ikea-kompis/component-keyboard';
import { Text } from '@inter-ikea-kompis/component-text';
import { ModalFooter as SkapaModalFooter, ModalHeader as SkapaModalHeader, Prompt as SkapaPrompt, Sheet as SkapaSheet } from '@ingka/modal-webc/passive.js';
import LawLabelModalEnum from '../../enums/LawLabelModalEnum.js';
import LawLabelUtility from '../../utilities/LawLabelUtility.js';
import LawLabelCard from '../law-label-card/LawLabelCard.js';
import LawLabelStyle from './LawLabelStyle.js';
/**
 * Law Label is a legal requirement in some markets. This law label is applicable for online sales of bedding & furniture products (US/Utah).
 *
 * The Law Label component consists of a link which is used in the related Kompis price display components. The link opens a separate dialogue with the law label image and disclaimer text if multiple law label images are displayed.
 *
 * When and where the Law Label should be displayed can be found in the {@link https://i-p-e-x.atlassian.net/wiki/spaces/BOK/pages/36972003409/Law+Labels+online+sales+of+bedding+furniture+products+US+Utah Range Experience BOK requirements}.
 *
 * @event modalOpen Triggered when the user clicks on the law label link
 * @event modalClose Triggered when the user clicks on the sheet close button.
 */
export default class LawLabel extends KompisElement {
    constructor() {
        super(...arguments);
        // Visible modal
        this.visibleModal = null;
        this.sheetElement = null;
        this.labelText = null;
    }
    render() {
        this.labelText = LawLabelUtility.getLawLabelLinkText(this.product);
        if (!this.labelText) {
            return null;
        }
        else {
            return html `
				<style>
					${LawLabelStyle.getStyle()}
				</style>

				${this.getLink()}
			`;
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.closeSheet();
    }
    async connectedCallback() {
        this.customElements.define('skapa-sheet', SkapaSheet);
        this.customElements.define('skapa-modal-footer', SkapaModalFooter);
        this.customElements.define('skapa-modal-header', SkapaModalHeader);
        this.customElements.define('skapa-prompt', SkapaPrompt);
        this.customElements.define('kompis-hyperlink', Hyperlink);
        this.customElements.define('kompis-text', Text);
        this.customElements.define('kompis-law-label-card', LawLabelCard);
        super.connectedCallback();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('visibleModal')) {
            if (this.visibleModal && this.visibleModal in LawLabelModalEnum) {
                this.openSheet();
            }
            else {
                this.closeSheet();
            }
        }
        return true;
    }
    getLink() {
        return html `
			<kompis-text .theme="${this.theme}" .type="${ThemeFontStyleTypeEnum.bodySmall}">
				<kompis-hyperlink
					.theme="${this.theme}"
					.text="${this.labelText}"
					.underline="${true}"
					@linkClick="${(event) => this.onLinkClick(event)}"
				>
				</kompis-hyperlink>
			</kompis-text>
		`;
    }
    onLinkClick(event) {
        event.preventDefault();
        this.dispatchEvent(new CustomEvent('modalOpen', {
            bubbles: true,
            detail: {
                visibleModal: LawLabelModalEnum.lawLabelCard
            }
        }));
    }
    closeSheet() {
        if (this.sheetElement) {
            this.sheetElement.open = false;
            this.sheetElement = null;
        }
    }
    openSheet() {
        if (this.sheetElement) {
            return;
        }
        const sheetElement = this.createSheetElement();
        this.sheetElement = sheetElement;
        const parent = KeyboardInjectorFinder.findKeyboardInjectorElement() || document.body;
        parent.appendChild(sheetElement);
        sheetElement.open = true;
    }
    createSheetElement() {
        const sheetElement = this.createSheet();
        const sheetHeaderElement = this.createSheetHeader();
        const lawLabelCardElement = this.createCard();
        sheetElement.appendChild(sheetHeaderElement);
        sheetElement.appendChild(lawLabelCardElement);
        return sheetElement;
    }
    createSheet() {
        const sheetElement = this.createInternalElement('skapa-sheet');
        sheetElement.setAttribute('aria-label', this.labelText || '');
        sheetElement.size = 'small';
        sheetElement.fullHeight = true;
        sheetElement.style.fontFamily = FontStyle.getFontFamily(this.theme);
        sheetElement.style.zIndex = '500';
        sheetElement.addEventListener('closerequest', () => {
            if (this.sheetElement) {
                this.dispatchEvent(new CustomEvent('modalClose', {
                    bubbles: true,
                    detail: {
                        visibleModal: null
                    }
                }));
            }
        });
        sheetElement.addEventListener('closed', () => {
            sheetElement.parentElement?.removeChild(sheetElement);
        });
        return sheetElement;
    }
    createSheetHeader() {
        const sheetHeaderElement = this.createInternalElement('skapa-modal-header');
        sheetHeaderElement.slot = 'header';
        const { modalAriaLabelButtonClose } = this.translations;
        const headerSpan = document.createElement('span');
        headerSpan.slot = 'closebutton-label';
        headerSpan.innerText = modalAriaLabelButtonClose;
        sheetHeaderElement.appendChild(headerSpan);
        return sheetHeaderElement;
    }
    createCard() {
        const cardElement = this.createInternalElement('kompis-law-label-card');
        cardElement.theme = this.theme;
        cardElement.product = this.product;
        cardElement.translations = this.translations;
        return cardElement;
    }
}
__decorate([
    property({ type: Object, required: true })
], LawLabel.prototype, "product", void 0);
__decorate([
    property({ type: Object, required: true })
], LawLabel.prototype, "translations", void 0);
__decorate([
    property({ type: Object, required: true })
], LawLabel.prototype, "theme", void 0);
__decorate([
    property({ type: String })
], LawLabel.prototype, "visibleModal", void 0);
//# sourceMappingURL=LawLabel.js.map